/**
 * @module @theme/wishlist-button
 *
 * Web component that toggles a product in/out of the wishlist.
 *
 * Usage:
 *   <wishlist-button-component
 *     data-handle="my-product"
 *     data-product-id="12345">
 *     ...children, including [ref="srLabel"] live region
 *   </wishlist-button-component>
 *
 * Reactively repaints state from `wishlist:update` events.
 */

import { Component } from '@theme/component';
import { wishlist, WishlistEvents } from '@theme/wishlist';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [button]
 * @property {HTMLElement} [srLabel]
 */

/** @extends {Component<Refs>} */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#paint();
    document.addEventListener(WishlistEvents.update, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistEvents.update, this.#onUpdate);
  }

  /** Declarative click handler — wired via `on:click="/toggle"` in Liquid. */
  toggle(event) {
    /* When inside <a class="product-card__link">, prevent navigation. */
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const handle = this.dataset.handle;
    if (!handle) return;
    const productId = this.dataset.productId || null;

    const nowActive = wishlist.toggle(handle, productId);

    /* Re-trigger the pulse animation by toggling [data-pulse] off/on. */
    this.removeAttribute('data-pulse');
    /* Force a reflow so the animation restarts cleanly. */
    void this.offsetWidth;
    this.setAttribute('data-pulse', '');

    /* Toast notification */
    const labels = (window.Theme && window.Theme.translations) || {};
    showToast({
      removed: !nowActive,
      title: nowActive ? labels.wishlist_added_title : labels.wishlist_removed_title,
      subtitle: nowActive ? labels.wishlist_added_subtitle : labels.wishlist_removed_subtitle,
      cta: nowActive
        ? { href: this.#wishlistUrl(), label: labels.wishlist_view || 'View' }
        : null,
    });
  }

  #wishlistUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.wishlist_url) || '/pages/wishlist';
  }

  #onUpdate = () => this.#paint();

  #paint() {
    const handle = this.dataset.handle;
    if (!handle) return;
    const active = wishlist.has(handle);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');

    const button = /** @type {HTMLElement|undefined} */ (this.refs.button);
    if (button) {
      const labelAdd = button.dataset.labelAdd || '';
      const labelRemove = button.dataset.labelRemove || '';
      button.setAttribute('aria-label', active ? labelRemove : labelAdd);
    }

    const sr = /** @type {HTMLElement|undefined} */ (this.refs.srLabel);
    if (sr) {
      const announceAdd = this.dataset.announceAdd || '';
      const announceRemove = this.dataset.announceRemove || '';
      sr.textContent = active ? announceAdd : announceRemove;
    }
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButton);
}

/* ── Toast helper ────────────────────────────────────────────
   Lightweight bottom-right toast that mirrors the visual language
   of cart / variant notifications. Renders into a single global
   container, creating it lazily on first use. */
function showToast({ removed = false, title = '', subtitle = '', cta = null, ttl = 2800 }) {
  const stack = ensureStack();
  const el = document.createElement('div');
  el.className = 'wishlist-toast';
  el.setAttribute('role', 'status');

  const iconHtml = removed
    ? '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6h12"/><path d="M7.5 6V4.5a1.5 1.5 0 0 1 1.5-1.5h2a1.5 1.5 0 0 1 1.5 1.5V6"/><path d="M5.5 6 6 16a1.5 1.5 0 0 0 1.5 1.5h5A1.5 1.5 0 0 0 14 16l.5-10"/></svg>'
    : '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 17.25S3.25 13.06 3.25 8.5a3.75 3.75 0 0 1 6.75-2.25 3.75 3.75 0 0 1 6.75 2.25c0 4.56-6.75 8.75-6.75 8.75Z"/></svg>';

  el.innerHTML =
    `<span class="wishlist-toast__icon" aria-hidden="true">${iconHtml}</span>` +
    `<span class="wishlist-toast__body">` +
      `<span class="wishlist-toast__title">${escapeHtml(title)}</span>` +
      `<span class="wishlist-toast__sub">${escapeHtml(subtitle)}</span>` +
    `</span>` +
    (cta ? `<a class="wishlist-toast__cta" href="${escapeAttr(cta.href)}">${escapeHtml(cta.label)}</a>` : '');

  stack.appendChild(el);
  requestAnimationFrame(() => el.setAttribute('data-show', ''));
  setTimeout(() => el.removeAttribute('data-show'), ttl);
  setTimeout(() => el.remove(), ttl + 400);
}

function ensureStack() {
  let stack = document.querySelector('.wishlist-toast-stack');
  if (stack instanceof HTMLElement) return stack;
  stack = document.createElement('div');
  stack.className = 'wishlist-toast-stack';
  stack.setAttribute('aria-live', 'polite');
  stack.setAttribute('aria-atomic', 'false');
  document.body.appendChild(stack);
  return stack;
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => {
    return /** @type {Record<string,string>} */ ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c];
  });
}

/** @param {string} s */
function escapeAttr(s) {
  return escapeHtml(s);
}
